import { lazy } from "react";
import { Routes, Route, Navigate } from "react-router";
import DashboardLayout from "@/components/layouts/dashboard/dashboard-layout";
import AuthLayout from "@/components/layouts/auth/auth-layout";

const isNotProduction = (): boolean => {
	return import.meta.env.VITE_ENV !== "production";
};

const EditCustomBlend = lazy(
	() => import("./routes/dashboard/library/custom-blends/edit-custom-blends"),
);
const ListCustomBlends = lazy(
	() =>
		import("@/app/routes/dashboard/library/custom-blends/list-custom-blends"),
);
const NewCustomBlend = lazy(
	() => import("@/app/routes/dashboard/library/custom-blends/new-custom-blend"),
);
const Free = lazy(() => import("@/app/routes/dashboard/library/free"));
const Collection = lazy(
	() => import("@/app/routes/dashboard/library/collection"),
);
const Effect = lazy(() => import("@/app/routes/dashboard/library/effect"));
const Album = lazy(() => import("@/app/routes/dashboard/library/album"));

const About = lazy(() => import("@/app/routes/dashboard/about"));
const AccountManagement = lazy(
	() => import("@/app/routes/dashboard/account-management"),
);
const Checkout = lazy(() => import("@/app/routes/dashboard/checkout"));
const CheckoutReview = lazy(
	() => import("@/app/routes/dashboard/checkout-review"),
);
const CheckoutConfirmation = lazy(
	() => import("@/app/routes/dashboard/checkout-confirmation"),
);
const EmailVerificationMessage = lazy(
	() => import("@/app/routes/auth/email-verification-message"),
);
const ResetEmail = lazy(() => import("@/app/routes/auth/reset-email"));
const Favorites = lazy(
	() => import("@/app/routes/dashboard/library/favorites"),
);
const ForgotPassword = lazy(() => import("@/app/routes/auth/forgot-password"));
const Home = lazy(() => import("@/app/routes/dashboard/home"));
const Index = lazy(() => import("@/app/routes/dashboard/index"));
const Library = lazy(() => import("@/app/routes/dashboard/library/library"));
const LogIn = lazy(() => import("@/app/routes/auth/log-in"));
const OtpLogIn = lazy(() => import("@/app/routes/auth/otp/log-in"));
const OtpVerify = lazy(() => import("@/app/routes/auth/otp/verify"));
const OtpWhoami = lazy(() => import("@/app/routes/auth/otp/whoami"));
const NotFound = lazy(() => import("@/app/routes/not-found"));
const ValidateAuth = lazy(() => import("@/app/routes/auth/validate-auth"));
const Pricing = lazy(() => import("@/app/routes/dashboard/pricing"));
const Rewards = lazy(() => import("@/app/routes/dashboard/rewards"));
const ResetPassword = lazy(() => import("@/app/routes/auth/reset-password"));
const SignUp = lazy(() => import("@/app/routes/auth/sign-up"));
const Start = lazy(() => import("@/app/routes/dashboard/start"));
const Track = lazy(() => import("@/app/routes/dashboard/library/track"));
const Terms = lazy(() => import("@/app/routes/dashboard/terms"));
const Writers = lazy(() => import("@/app/routes/dashboard/made-for/writers"));
const Students = lazy(() => import("@/app/routes/dashboard/made-for/students"));
const RemoteWorkers = lazy(
	() => import("@/app/routes/dashboard/made-for/remote-workers"),
);
const Developers = lazy(
	() => import("@/app/routes/dashboard/made-for/developers"),
);
const Teachers = lazy(() => import("@/app/routes/dashboard/made-for/teachers"));
const AudioEngineMixPrototype = lazy(
	() => import("@/app/routes/dashboard/prototype/audio-engine-mix"),
);
const AudioEngineStandalonePrototype = lazy(
	() => import("@/app/routes/dashboard/prototype/audio-engine-standalone"),
);

export const AppRouter = () => {
	return (
		<Routes>
			<Route element={<DashboardLayout />}>
				<Route path="/" element={<Index />} />
				<Route path="home" element={<Home />} />
				{/* <Route path="test" element={<Test />} /> */}
				<Route path="about" element={<About />} />
				<Route path="account" element={<AccountManagement />} />
				<Route path="checkout" element={<Checkout />} />
				<Route path="checkout-review" element={<CheckoutReview />} />
				<Route
					path="checkout-confirmation"
					element={<CheckoutConfirmation />}
				/>
				<Route path="library">
					<Route index element={<Library />} />
					<Route path="custom-blend">
						<Route index element={<ListCustomBlends />} />
						<Route path="create" element={<NewCustomBlend />} />
					</Route>
					<Route path="custom-blends">
						<Route index element={<ListCustomBlends />} />
						<Route path="create" element={<NewCustomBlend />} />
					</Route>
					<Route path="favorites" element={<Favorites />} />
					<Route path="free" element={<Free />} />
					<Route path="effect/:slug" element={<Effect />} />
					<Route path="effects/:slug" element={<Effect />} />
					<Route
						path="collection/free"
						element={<Navigate to="/library/free" />}
					/>
					<Route path="collection/:slug" element={<Collection />} />
					<Route path="collections/:slug" element={<Collection />} />
					<Route path="album/:slug" element={<Album />} />
					<Route path="albums/:slug" element={<Album />} />
					<Route path="track/:slug" element={<Track />} />
					<Route path="tracks/:slug" element={<Track />} />
					<Route path="track/:slug/edit" element={<EditCustomBlend />} />
					<Route path="tracks/:slug/edit" element={<EditCustomBlend />} />
				</Route>
				<Route path="made-for">
					<Route path="students" element={<Students />} />
					<Route path="writers" element={<Writers />} />
					<Route path="remote-workers" element={<RemoteWorkers />} />
					<Route path="developers" element={<Developers />} />
					<Route path="teachers" element={<Teachers />} />
				</Route>
				<Route path="rewards" element={<Rewards />} />
				<Route path="pricing">
					<Route index element={<Pricing />} />
				</Route>
				{isNotProduction() && (
					<>
						<Route
							path="prototype/audio-engine-standalone"
							element={<AudioEngineStandalonePrototype />}
						/>
						<Route
							path="prototype/audio-engine-mix"
							element={<AudioEngineMixPrototype />}
						/>
					</>
				)}
				<Route path="start" element={<Start />} />
				<Route path="terms" element={<Terms />} />
				<Route
					path="user/favorites"
					element={<Navigate to="/library/favorites" />}
				/>
			</Route>
			<Route path="auth" element={<AuthLayout />}>
				<Route
					path="email-verification-message"
					element={<EmailVerificationMessage />}
				/>
				<Route path="log-in" element={<LogIn />} />
				<Route path="sign-up" element={<SignUp />} />
				<Route path="forgot-password" element={<ForgotPassword />} />
				<Route path="validate-auth" element={<ValidateAuth />} />
				<Route path="reset-email" element={<ResetEmail />} />
				<Route path="reset-password" element={<ResetPassword />} />

				{isNotProduction() && (
					<Route path="otp">
						<Route path="log-in" element={<OtpLogIn />} />
						<Route path="verify" element={<OtpVerify />} />
						<Route path="whoami" element={<OtpWhoami />} />
					</Route>
				)}
			</Route>
			<Route path="*" element={<NotFound />} />
		</Routes>
	);
};
